import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { invariant } from '@epic-web/invariant';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { DealBookingType, DealProductBuilderRow } from '@src/types/OTC';
import {
  isFixedRateLoanProductRow,
  isFloatingRateLoanProductRow,
  isForwardProductRow,
  isOptionProductRow,
  isRepoProductRow,
} from '@src/types/OTC/otcGuards';
import {
  ACTION,
  Button,
  ButtonVariants,
  FormControlSizes,
  FormMessage,
  HStack,
  logger,
  useGlobalToasts,
  useSecuritiesContext,
  VStack,
} from '@talos/kyoko';
import { useDealProductBuilder } from './DealProductBuilderProvider/useDealProductBuilder';
import { useCreateCustomInstrumentMutation, type CustomInstrumentPostBody } from './endpoints/customInstrumentEndpoint';
import { useSymbolPriming } from './hooks';
import { FixedRateLoanProductDefinition } from './productDefinitions/FixedRateLoanProductDefinition';
import { FloatingRateLoanProductDefinition } from './productDefinitions/FloatingRateLoanProductDefinition';
import { ForwardProductDefinition } from './productDefinitions/ForwardProductDefinition';
import { OptionProductDefinition } from './productDefinitions/OptionProductDefinition';
import { RepoProductDefinition } from './productDefinitions/RepoProductDefinition';

interface DealProductBuilderActionsProps {
  rowData: DealProductBuilderRow[];
  bookingType: DealBookingType;
  isFormValid: boolean;
}

type ValidationResult = {
  error: ReactNode;
  type: 'warning' | 'error';
} | null;

// Create specific types for each product type using InferType

const forwardProductDefinition = new ForwardProductDefinition();
const loanProductDefinition = new FixedRateLoanProductDefinition();
const optionProductDefinition = new OptionProductDefinition();
const floatingRateLoanProductDefinition = new FloatingRateLoanProductDefinition();
const repoProductDefinition = new RepoProductDefinition();

const useInstrumentUniquenessValidation = (rowData: DealProductBuilderRow[]): ValidationResult => {
  const { enableOTCDeltaStrikeSupport } = useFeatureFlag();

  const {
    otcForwardProductTemplatesByDisplaySymbol,
    otcFixedRateLoanProductTemplatesByDisplaySymbol,
    otcOptionProductTemplatesByDisplaySymbol,
    otcFloatingRateLoanProductTemplatesByDisplaySymbol,
    otcRepoProductTemplatesByDisplaySymbol,
  } = useSecuritiesContext();

  return useMemo((): ValidationResult => {
    // Update singletons here (not in a useEffect) so reference data is current
    // when the check runs — useEffect fires after render, after useMemo.
    forwardProductDefinition.updateReferenceData(otcForwardProductTemplatesByDisplaySymbol);
    loanProductDefinition.updateReferenceData(otcFixedRateLoanProductTemplatesByDisplaySymbol);
    optionProductDefinition.updateReferenceData(otcOptionProductTemplatesByDisplaySymbol);
    optionProductDefinition.setEnableOTCDeltaStrikeSupport(enableOTCDeltaStrikeSupport);
    floatingRateLoanProductDefinition.updateReferenceData(otcFloatingRateLoanProductTemplatesByDisplaySymbol);
    repoProductDefinition.updateReferenceData(otcRepoProductTemplatesByDisplaySymbol);

    const targetRow = rowData.at(0);
    if (!targetRow?.productTemplate) {
      return null;
    }

    let result: 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated';

    if (isForwardProductRow(targetRow)) {
      result = forwardProductDefinition.checkForUniqueness(targetRow);
    } else if (isFixedRateLoanProductRow(targetRow)) {
      result = loanProductDefinition.checkForUniqueness(targetRow);
    } else if (isOptionProductRow(targetRow)) {
      result = optionProductDefinition.checkForUniqueness(targetRow);
    } else if (isFloatingRateLoanProductRow(targetRow)) {
      result = floatingRateLoanProductDefinition.checkForUniqueness(targetRow);
    } else if (isRepoProductRow(targetRow)) {
      result = repoProductDefinition.checkForUniqueness(targetRow);
    } else {
      return null;
    }

    // Construct the full symbol including suffix for error messages
    const fullSymbol = targetRow.uniqueSuffix
      ? `${targetRow.productTemplate}-${targetRow.uniqueSuffix}`
      : targetRow.productTemplate;

    if (result === 'notReady') {
      return null;
    } else if (result === 'conflict') {
      // Conflict means same symbol but different details - disable until unique suffix is added
      return {
        error: (
          <>
            Product &quot;{fullSymbol}&quot; is in conflict with an existing product definition.
            <br />
            Use the suffix to create a unique product if desired.
          </>
        ),
        type: 'error', // Disable prime booking until suffix makes product unique
      };
    } else if (result === 'alreadyCreated') {
      return {
        error: `Product "${fullSymbol}" is already defined`,
        type: 'error', // Disable prime booking when suffix matches existing product
      };
    }
    return null;
  }, [
    rowData,
    otcForwardProductTemplatesByDisplaySymbol,
    otcFixedRateLoanProductTemplatesByDisplaySymbol,
    otcOptionProductTemplatesByDisplaySymbol,
    otcFloatingRateLoanProductTemplatesByDisplaySymbol,
    otcRepoProductTemplatesByDisplaySymbol,
    enableOTCDeltaStrikeSupport,
  ]);
};

const useAssetMismatchValidation = (
  rowData: DealProductBuilderRow[],
  validationErrors: Record<string, string | string[]> | null
): ValidationResult => {
  return useMemo((): ValidationResult => {
    const targetRow = rowData.at(0);
    if (
      !targetRow ||
      (!isFixedRateLoanProductRow(targetRow) &&
        !isFloatingRateLoanProductRow(targetRow) &&
        !isRepoProductRow(targetRow))
    ) {
      return null;
    }

    if (!validationErrors) {
      return null;
    }

    // Use validation errors from state (already computed by yup validation in slice)
    // Check both baseAsset and collateralAsset since validation can fail on either field
    if (validationErrors.baseAsset) {
      const error = Array.isArray(validationErrors.baseAsset)
        ? validationErrors.baseAsset[0]
        : validationErrors.baseAsset;
      return {
        error,
        type: 'error',
      };
    }
    if (validationErrors.collateralAsset) {
      const error = Array.isArray(validationErrors.collateralAsset)
        ? validationErrors.collateralAsset[0]
        : validationErrors.collateralAsset;
      return {
        error,
        type: 'error',
      };
    }

    // Check for schema-level test errors (like 'loan-pair-has-fixing')
    // Schema-level errors may be stored at the root level or under a special key
    // Look for errors containing the fixing validation message
    const allErrorMessages = Object.values(validationErrors).flat();
    const fixingError = allErrorMessages.find(
      msg =>
        typeof msg === 'string' && (msg.includes('fixing') || msg.includes('Borrowed Asset and Collateral Asset pair'))
    );
    if (fixingError) {
      return {
        error: fixingError,
        type: 'error',
      };
    }

    return null;
  }, [rowData, validationErrors]);
};

export const DealProductBuilderActions = ({ rowData, bookingType, isFormValid }: DealProductBuilderActionsProps) => {
  const { add: addToast } = useGlobalToasts();
  const [createCustomInstrumentMutation] = useCreateCustomInstrumentMutation();
  const {
    dispatch,
    actions: { clearForm },
    state: { validationErrors },
  } = useDealProductBuilder();

  const instrumentValidationState = useInstrumentUniquenessValidation(rowData);
  const assetMismatchValidationState = useAssetMismatchValidation(rowData, validationErrors);
  const [isSaving, setIsSaving] = useState(false);

  const { primeSymbol } = useSymbolPriming(bookingType);

  const createProduct = useCallback(
    async (rowData: DealProductBuilderActionsProps['rowData']) => {
      const rowDataToCheck = rowData.at(0);
      try {
        invariant(rowDataToCheck, 'Product builder row data is empty');
        const createCustomInstrument = (body: CustomInstrumentPostBody) =>
          createCustomInstrumentMutation(body).unwrap();
        let result:
          | {
              symbol: string;
            }
          | undefined;

        if (isForwardProductRow(rowDataToCheck)) {
          result = await forwardProductDefinition.createProduct({
            rowData: rowDataToCheck,
            createCustomInstrument,
            onSuccess: message => addToast({ text: message, variant: 'DEFAULT' }),
            onError: message => addToast({ text: message, variant: 'NEGATIVE' }),
          });
        } else if (isFixedRateLoanProductRow(rowDataToCheck)) {
          result = await loanProductDefinition.createProduct({
            rowData: rowDataToCheck,
            createCustomInstrument,
            onSuccess: message => addToast({ text: message, variant: 'DEFAULT' }),
            onError: message => addToast({ text: message, variant: 'NEGATIVE' }),
          });
        } else if (isOptionProductRow(rowDataToCheck)) {
          result = await optionProductDefinition.createProduct({
            rowData: rowDataToCheck,
            createCustomInstrument,
            onSuccess: message => addToast({ text: message, variant: 'DEFAULT' }),
            onError: message => addToast({ text: message, variant: 'NEGATIVE' }),
          });
        } else if (isFloatingRateLoanProductRow(rowDataToCheck)) {
          result = await floatingRateLoanProductDefinition.createProduct({
            rowData: rowDataToCheck,
            createCustomInstrument,
            onSuccess: message => addToast({ text: message, variant: 'DEFAULT' }),
            onError: message => addToast({ text: message, variant: 'NEGATIVE' }),
          });
        } else if (isRepoProductRow(rowDataToCheck)) {
          result = await repoProductDefinition.createProduct({
            rowData: rowDataToCheck,
            createCustomInstrument,
            onSuccess: message => addToast({ text: message, variant: 'DEFAULT' }),
            onError: message => addToast({ text: message, variant: 'NEGATIVE' }),
          });
        } else {
          // this should never happen typewise (rowDataCheck is a never, but just for safety)
          throw new Error(`Unsupported product type - see detailed error`);
        }

        if (result?.symbol) {
          await primeSymbol(result.symbol);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An unexpected error occurred while creating the product';
        addToast({
          text: `Failed to create ${rowDataToCheck?.product ?? 'product'}: ${errorMessage}`,
          variant: 'NEGATIVE',
        });
        logger.error(new Error(`Error creating OTC ${rowDataToCheck?.product ?? 'Unknown product'}`), {
          extra: {
            input: { rowData },
            error,
          },
        });
      }
    },
    [addToast, createCustomInstrumentMutation, primeSymbol]
  );

  const handleSaveProduct = useCallback(async () => {
    setIsSaving(true);
    try {
      await createProduct(rowData);
    } finally {
      // Always clear saving state regardless of success or failure
      setIsSaving(false);
    }
  }, [createProduct, rowData]);

  const handleClear = useCallback(() => {
    dispatch(clearForm(bookingType));

    addToast({
      text: 'Form cleared successfully',
      variant: 'DEFAULT',
    });
  }, [dispatch, clearForm, bookingType, addToast]);

  const { isAuthorized } = useRoleAuth();

  const isSaveDisabled =
    !isAuthorized(ACTION.CREATE_CUSTOM_OTC_INSTRUMENT) ||
    isSaving ||
    !isFormValid ||
    (!!instrumentValidationState && instrumentValidationState.type === 'error') ||
    !!assetMismatchValidationState;

  return (
    <VStack gap="spacing8" alignItems="stretch">
      <HStack gap="spacing12" p="spacing8" justifyContent="flex-end" background="colors.gray.020" my={1}>
        <Button variant={ButtonVariants.Default} size={FormControlSizes.Small} onClick={handleClear}>
          Clear
        </Button>
        <Button
          variant={ButtonVariants.Primary}
          size={FormControlSizes.Small}
          disabled={isSaveDisabled}
          loading={isSaving}
          onClick={handleSaveProduct}
        >
          Prime Booking
        </Button>
      </HStack>
      {(instrumentValidationState || assetMismatchValidationState) && !isSaving && (
        <>
          {instrumentValidationState && (
            <FormMessage
              data-testid="instrument-validation-message"
              warning={instrumentValidationState.type === 'warning'}
              fontSize="fontSizeSm"
              px="spacing8"
              textAlign="right"
              color={instrumentValidationState.type === 'warning' ? 'colors.gray.080' : undefined}
            >
              {instrumentValidationState.error}
            </FormMessage>
          )}
          {assetMismatchValidationState && (
            <FormMessage
              data-testid="asset-mismatch-validation-message"
              warning={false}
              fontSize="fontSizeSm"
              px="spacing8"
              textAlign="right"
            >
              {assetMismatchValidationState.error}
            </FormMessage>
          )}
        </>
      )}
    </VStack>
  );
};
